import React, { useEffect, useRef, useState } from 'react';
import Typed from 'typed.js';
import { motion } from 'framer-motion';
import {
  FaEnvelope,
  FaCalendarAlt,
  FaArrowRight,
  FaClock,
  FaWhatsapp,
  FaPhoneAlt,
  FaShieldAlt,
  FaPaintBrush,
  FaHashtag,
  FaChartLine,
  FaLaptop,
  FaSearch,
  FaChartBar,
  FaLightbulb,
  FaCheckCircle,
  FaStar,
  FaChevronRight,
  FaRegLightbulb,
  FaNetworkWired,
  FaBlog,
  FaHome,
  FaRocket,
  FaEnvelopeOpen,
  FaUsers,
  FaGlobe,
  FaBullhorn,
  FaShoppingCart,
  FaMapMarkerAlt,
  FaQuoteLeft,
  FaEye,
  FaHandshake,
  FaCogs,
  FaTag,
  FaArrowUp,
  FaPlay,
  FaPause
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

// Import brand images
import africaUncut from '../assets/images/Brands/AfricaUncut.jpg';
import aMasyaAdvocates from '../assets/images/Brands/A-masyaAdvocates.jpg';
import aqualPalSprings from '../assets/images/Brands/AqualPal Springs Logo.jpg';
import bts from '../assets/images/Brands/bts.jpeg';
import daishCollection from '../assets/images/Brands/daishcollection.jpg';
import daishGroup from '../assets/images/Brands/Daishgroup.png';
import fractional from '../assets/images/Brands/fractional.jpg';
import kibeAfrica from '../assets/images/Brands/kibeafric.jpg';
import masaiSafariCamp from '../assets/images/Brands/masaisafaricamp.png';
import newDaish from '../assets/images/Brands/newdaish.jpeg';
import npLogo from '../assets/images/Brands/npLogo.jpg';
import theGreatAdventures from '../assets/images/Brands/thegreatadventures.png';
import tsavoCats from '../assets/images/Brands/tsavocats.png';
import wildNest from '../assets/images/Brands/wildnest.png';
import savannahnest from '../assets/images/Brands/savannahnest.png';
import tallmasai from '../assets/images/Brands/tallmasai.jpg';

const Home = () => {
  const typedRef = useRef(null);
  const carouselRef = useRef(null);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  useEffect(() => {
    const typed = new Typed(typedRef.current, {
      strings: [
        'Strategy & Automation',
        'Ready-to-Use Business Systems',
        'High-Converting Websites',
        'Lead Generation Frameworks',
        'Scalable Digital Growth'
      ],
      typeSpeed: 70,
      backSpeed: 45,
      backDelay: 2400,
      loop: true,
      cursorChar: '▌',
      smartBackspace: true
    });
  
    return () => typed.destroy();
  }, []);

  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || isCarouselPaused) return;

    const scrollInterval = setInterval(() => {
      if (carousel.scrollLeft + carousel.clientWidth >= carousel.scrollWidth - 10) {
        carousel.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        carousel.scrollBy({ left: 1, behavior: 'smooth' });
      }
    }, 30);

    return () => clearInterval(scrollInterval);
  }, [isCarouselPaused]);

  const services = [
    { 
      id: 1,
      icon: <FaLaptop className="w-6 h-6" />, 
      title: 'Web Development', 
      desc: 'Custom websites with cutting-edge technology and CMS integration',
      features: ['Responsive Design', 'SEO-Optimized', 'E-commerce', 'Fast Loading'],
      stats: ['98% Client Satisfaction', '40% Faster Load Times']
    },
    { 
      id: 2,
      icon: <FaSearch className="w-6 h-6" />, 
      title: 'SEO Strategy', 
      desc: 'Comprehensive SEO services that dominate Kenyan search results',
      features: ['Keyword Research', 'Technical SEO', 'Local SEO', 'Analytics'],
      stats: ['Avg 3.2x ROI', 'Top 3 Rankings']
    },
    { 
      id: 3,
      icon: <FaHashtag className="w-6 h-6" />, 
      title: 'Social Media', 
      desc: 'Strategic social campaigns that engage and convert your audience',
      features: ['Content Strategy', 'Community Mgmt', 'Paid Ads', 'Analytics'],
      stats: ['50%+ Engagement', '10K Followers/Month']
    },
    { 
      id: 4,
      icon: <FaPaintBrush className="w-6 h-6" />, 
      title: 'Brand Design', 
      desc: 'Cohesive visual identity that communicates your brand story',
      features: ['Logo Design', 'Brand Guidelines', 'UI/UX', 'Marketing Materials'],
      stats: ['2x Brand Recall', 'Professional Impact']
    },
    { 
      id: 5,
      icon: <FaChartLine className="w-6 h-6" />, 
      title: 'Digital Strategy', 
      desc: 'Data-driven marketing plans with measurable ROI objectives',
      features: ['Market Analysis', 'KPI Tracking', 'ROI Optimization', 'Reporting'],
      stats: ['48-Hour Strategy', 'Measurable Results']
    },
    { 
      id: 6,
      icon: <FaEnvelope className="w-6 h-6" />, 
      title: 'Email Marketing', 
      desc: 'Automated email sequences that nurture leads into customers',
      features: ['List Building', 'Automation', 'A/B Testing', 'Analytics'],
      stats: ['78% More Leads', '35% Open Rates']
    }
  ];

  const trustedBrands = [
    { id: 1, name: 'Africa Uncut', logo: africaUncut },
    { id: 2, name: 'A-Masya Advocates', logo: aMasyaAdvocates },
    { id: 3, name: 'AqualPal Springs', logo: aqualPalSprings },
    { id: 4, name: 'BTS', logo: bts },
    { id: 5, name: 'Daish Collection', logo: daishCollection },
    { id: 6, name: 'Daish Group', logo: daishGroup },
    { id: 7, name: 'Fractional', logo: fractional },
    { id: 8, name: 'Kibe Africa', logo: kibeAfrica },
    { id: 9, name: 'Masai Safari Camp', logo: masaiSafariCamp },
    { id: 10, name: 'New Daish', logo: newDaish },
    { id: 12, name: 'NP Logo', logo: npLogo },
    { id: 13, name: 'The Great Adventures', logo: theGreatAdventures },
    { id: 14, name: 'Tsavo Cats', logo: tsavoCats },
    { id: 15, name: 'Wild Nest', logo: wildNest },
    { id: 16, name: 'Savannah Nest', logo: savannahnest },
    { id: 17, name: 'Tall Masai', logo: tallmasai }
  ];

  const carouselBrands = [...trustedBrands, ...trustedBrands];

  const readySystems = [
    {
      id: 1,
      icon: <FaGlobe className="w-6 h-6" />,
      category: 'Tourism & Hospitality',
      name: 'Tour Management System',
      description: 'A ready-made digital system for Kenyan tour operators, featuring booking management, customer communication, and itinerary planning.',
      features: ['Booking Management', 'Customer Communication', 'Itinerary Planning', 'Payment Integration']
    },
    {
      id: 2,
      icon: <FaBlog className="w-6 h-6" />,
      category: 'Content Marketing',
      name: 'E-commerce with Sales & Admin Dashboard (CRM)',
      description: 'A comprehensive content marketing system designed to help Kenyan businesses create, distribute, and optimize content for maximum engagement.',
      features: ['Content Calendar', 'SEO Optimization', 'Distribution Tools', 'Performance Analytics']
    },
    {
      id: 3,
      icon: <FaHome className="w-6 h-6" />,
      category: 'Real Estate',
      name: 'Kenya Real Estate CRM',
      description: 'A ready-made CRM system for Kenyan real estate agents, featuring lead management, property listings, and client communication tools.',
      features: ['Lead Tracking', 'Property Management', 'Client Communication', 'Analytics Dashboard']
    }
  ];

  const processSteps = [
    { 
      num: '01', 
      title: 'Discovery & Analysis', 
      desc: 'Deep market research and competitive analysis',
      icon: <FaRegLightbulb className="w-8 h-8" />,
      details: ['Market Analysis', 'Competitor Audit', 'SWOT Analysis', 'Goal Setting']
    },
    { 
      num: '02', 
      title: 'Strategy Design', 
      desc: 'Custom digital roadmap with KPIs and milestones',
      icon: <FaNetworkWired className="w-8 h-8" />,
      details: ['Strategy Development', 'KPI Definition', 'Timeline Planning', 'Budget Allocation']
    },
    { 
      num: '03', 
      title: 'Execution Excellence', 
      desc: 'Agile implementation with weekly progress reviews',
      icon: <FaCogs className="w-8 h-8" />,
      details: ['Agile Development', 'Content Creation', 'Campaign Launch', 'Weekly Reporting']
    },
    { 
      num: '04', 
      title: 'Optimization Cycle', 
      desc: 'Continuous A/B testing and performance enhancement',
      icon: <FaChartBar className="w-8 h-8" />,
      details: ['Performance Analysis', 'A/B Testing', 'Strategy Refinement', 'ROI Optimization']
    }
  ];

  const ServiceCard = ({ service, index }) => (
    <motion.div
      className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-[#fdaa01] overflow-hidden"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      whileHover={{ y: -10 }}
    >
      {/* Brand gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#010536] to-[#020a5c] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <div className="relative z-10">
        <div className="flex flex-col items-center text-center mb-6">
          <div 
            className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500"
            style={{ backgroundColor: 'rgba(253, 170, 1, 0.1)' }}
          >
            <div className="text-3xl text-[#fdaa01] group-hover:text-[#fdaa01] transition-colors duration-500">
              {service.icon}
            </div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#fdaa01] transition-colors duration-500">
            {service.title}
          </h3>
        </div>
        
        <p className="text-gray-600 leading-relaxed mb-5 text-center group-hover:text-white/90 transition-colors duration-500">
          {service.desc}
        </p>
        
        <div className="mb-5 space-y-2">
          {service.features.slice(0, 3).map((feature, fIdx) => (
            <div key={fIdx} className="flex items-center text-gray-700 group-hover:text-white/90 transition-colors duration-500">
              <FaCheckCircle className="mr-2 flex-shrink-0 text-[#fdaa01]" />
              <span className="text-sm">{feature}</span>
            </div>
          ))}
        </div>
        
        <div className="flex flex-col gap-2 pt-4 border-t border-gray-100 group-hover:border-white/20 transition-colors duration-500">
          {service.stats.map((stat, sIdx) => (
            <div key={sIdx} className="flex items-center text-sm text-gray-600 group-hover:text-white/90 transition-colors duration-500">
              <FaStar className="mr-2 text-xs text-[#fdaa01]" />
              <span>{stat}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="relative overflow-x-hidden bg-[#010536]">
      {/* Modern Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-[#020a5c] via-[#010536] to-[#000324] text-white overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(99,102,241,0.15)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(253,170,1,0.1)_0%,transparent_50%),radial-gradient(circle_at_40%_40%,rgba(59,130,246,0.1)_0%,transparent_50%)] animate-pulse" />
        </div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-5 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#fdaa01]/10 to-[#010536]/10 blur-[40px] animate-float" />
          <div className="absolute top-60 right-10 w-[200px] h-[200px] rounded-full bg-gradient-to-br from-[#010536]/10 to-[#fdaa01]/10 blur-[40px] animate-float [animation-delay:5s]" />
          <div className="absolute bottom-20 left-20 w-[150px] h-[150px] rounded-full bg-gradient-to-br from-[#fdaa01]/10 to-[#010536]/10 blur-[40px] animate-float [animation-delay:10s]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Hero Content */}
            <motion.div
              className="text-center lg:text-left order-2 lg:order-1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-[#fdaa01]/30 rounded-full px-4 py-2 mb-6"
              >
                <span className="w-2 h-2 rounded-full bg-[#fdaa01] animate-pulse" />
                <span className="text-sm text-white/90">Available for New Projects</span>
              </motion.div>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-tight mb-8">
                <span className="bg-gradient-to-r from-white via-white to-white/80 bg-clip-text text-transparent">
                  Digital Systems That Help
                </span>
                <span className="block mt-2 bg-gradient-to-r from-[#fdaa01] to-[#ffc042] bg-clip-text text-transparent">
                  Kenyan SMEs Grow Faster
                </span>
                
                <div className="block mt-6 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#fdaa01]">
                  Built with{' '}
                  <span ref={typedRef} className="inline-block min-w-[200px] border-b-2 border-[#fdaa01]/50" />
                </div>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0">
                We design websites, marketing, and ready-made digital systems 
                that help small and growing businesses attract clients, 
                convert leads, and operate more efficiently, without hiring a full tech team.
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8 justify-center lg:justify-start">
                {[
                  { icon: <FaEnvelopeOpen />, text: 'Lead-Driven Email Systems' },
                  { icon: <FaBullhorn />, text: 'Content & Social Growth' },
                  { icon: <FaPaintBrush />, text: 'Conversion-Focused Design' },
                  { icon: <FaGlobe />, text: 'High-Performance Websites' }
                ].map((tag, idx) => (
                  <motion.div 
                    key={idx}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/20 backdrop-blur-sm text-sm hover:bg-[#fdaa01]/20 hover:border-[#fdaa01] hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 cursor-pointer"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4 + idx * 0.1 }}
                  >
                    <span className="text-[#fdaa01]">{tag.icon}</span>
                    {tag.text}
                  </motion.div>
                ))}
              </div>
              
              <div className="flex gap-4 sm:gap-6 mb-10 justify-center lg:justify-start flex-wrap">
                {[
                  { value: '78%', label: 'Lead Growth', delay: 0.3 },
                  { value: '65%', label: 'Conversions', delay: 0.4 },
                  { value: '40%+', label: 'Engagement', delay: 0.5 }
                ].map((stat, idx) => (
                  <motion.div
                    key={idx}
                    className="flex-1 max-w-[120px] text-center bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:border-[#fdaa01]/50 transition-all duration-300"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: stat.delay }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="text-2xl font-extrabold text-[#fdaa01]">
                      {stat.value}
                    </div>
                    <div className="text-sm text-white/80 mt-1">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center lg:justify-start">
                <motion.a
                  href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaCalendarAlt className="mr-2 group-hover:rotate-12 transition-transform duration-300" />
                  Book a Free Strategy Call
                </motion.a>
                
                <a
                  href="https://wa.me/254762272152"
                  target="_blank" 
                  rel="noreferrer"
                  className="group inline-flex items-center justify-center px-8 py-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all duration-300"
                >
                  <FaWhatsapp className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  Request a Quick Estimate
                </a>
              </div>
              
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-sm text-white/80 bg-white/5 px-4 py-2 rounded-full backdrop-blur-sm hover:bg-[#fdaa01]/20 hover:border-[#fdaa01] border border-transparent transition-all duration-300">
                  <FaChartLine className="text-[#fdaa01]" />
                  <span>Proven ROI-Focused Strategies</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/80 bg-white/5 px-4 py-2 rounded-full backdrop-blur-sm hover:bg-[#fdaa01]/20 hover:border-[#fdaa01] border border-transparent transition-all duration-300">
                  <FaShieldAlt className="text-[#fdaa01]" />
                  <span>Structured Process</span>
                </div>
              </div>
            </motion.div>
            
            {/* Hero Visual */}
            <motion.div
              className="order-1 lg:order-2 max-w-xl mx-auto w-full"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/90 rounded-2xl p-5 sm:p-6 border border-[#fdaa01]/20 shadow-2xl backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#fdaa01] via-[#010536] to-[#fdaa01] rounded-t-2xl" />
                
                <div className="bg-slate-900 rounded-xl overflow-hidden border border-white/10">
                  <div className="bg-slate-900/80 px-4 py-3 border-b border-white/10 flex items-center justify-between">
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500 hover:bg-red-400 transition-colors cursor-pointer" />
                      <span className="w-2.5 h-2.5 rounded-full bg-[#fdaa01] hover:bg-[#ffc042] transition-colors cursor-pointer" />
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 transition-colors cursor-pointer" />
                    </div>
                    <span className="text-xs text-white/70 truncate">SME Growth Dashboard</span>
                  </div>
                  
                  <div className="p-4 sm:p-5">
                    <div className="space-y-3">
                      {[
                        { icon: <FaUsers />, label: 'Leads This Month', value: '140', change: '↑ 78%' },
                        { icon: <FaShoppingCart />, label: 'Conversion Rate', value: '4.8%', change: '↑ 65%' },
                        { icon: <FaChartLine />, label: 'Revenue Growth', value: '215%', change: '↑ This Quarter' }
                      ].map((metric, idx) => (
                        <motion.div 
                          key={idx} 
                          className="flex items-center gap-3 py-2 border-b border-white/5 hover:bg-white/5 rounded-lg px-2 transition-colors duration-300"
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.6 + idx * 0.2 }}
                        >
                          <span className="text-[#fdaa01] text-sm">{metric.icon}</span>
                          <span className="flex-1 text-sm text-white/80">{metric.label}</span>
                          <span className="font-bold text-white">{metric.value}</span>
                          <span className="text-xs font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                            {metric.change}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    
                    <div className="flex gap-3 items-end h-24 mt-4 p-3 bg-slate-800/30 rounded-lg">
                      {[
                        { label: 'Leads', height: '78%' },
                        { label: 'Sales', height: '65%' },
                        { label: 'Engage', height: '40%' }
                      ].map((bar, idx) => (
                        <motion.div 
                          key={idx}
                          className="flex-1 bg-gradient-to-t from-[#010536] to-[#fdaa01] rounded-t"
                          style={{ height: bar.height }}
                          initial={{ height: 0 }}
                          animate={{ height: bar.height }}
                          transition={{ delay: 0.8 + idx * 0.2, duration: 0.5 }}
                        >
                          <span className="block text-center text-[10px] text-white/60 mt-1">{bar.label}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="px-4 py-3 border-t border-white/10 flex justify-center">
                    <div className="flex items-center gap-1.5 bg-[#fdaa01]/10 px-3 py-1.5 rounded-full text-xs border border-[#fdaa01]/30 hover:bg-[#fdaa01]/20 transition-colors duration-300">
                      <FaMapMarkerAlt className="text-[#fdaa01]" />
                      <span>SME Dashboard</span>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="mt-6 bg-gradient-to-br from-slate-800/70 to-slate-900/80 rounded-xl p-5 border-l-4 border-[#fdaa01] backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <FaQuoteLeft className="text-[#fdaa01]" />
                  <span className="font-semibold text-sm flex-1">Eshter Kariuki, Nairobi</span>
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="text-[#fdaa01] text-xs animate-pulse" style={{ animationDelay: `${i * 0.1}s` }} />
                    ))}
                  </div>
                </div>
                <p className="italic text-white/90 leading-relaxed text-sm mb-3">
                  "The Camp Management System brought us more flexibility in managing the camp's bookings which led to high-quality leads in our first months. It is a Game-changer for our service business!"
                </p>
                <div className="text-xs text-white/60">Campsite Business</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c] relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#fdaa01]/30 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
              <FaTag className="mr-2" />
              Our Core Services
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#fdaa01]">
              Comprehensive Digital Solutions
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto mt-4" />
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
          
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <Link 
              to="/services"
              className="group inline-flex items-center px-8 py-4 rounded-full border-2 border-[#fdaa01] text-[#fdaa01] font-semibold hover:bg-[#fdaa01] hover:text-[#010536] transition-all duration-300"
            >
              <FaEye className="mr-2 group-hover:scale-110 transition-transform duration-300" />
              View All Services
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trusted Brands Carousel */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#010536] mb-4 px-4 py-2 bg-[#010536]/10 rounded-full border border-[#010536]/20">
              <FaHandshake className="mr-2" />
              Our Partners
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              Trusted by Industry Leaders
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              We've built digital systems, websites, and online presence for these amazing brands
            </p>
          </motion.div>

          <div className="relative mb-12">
            <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-r from-slate-50 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-l from-slate-50 to-transparent z-10 pointer-events-none" />
            
            <div 
              ref={carouselRef}
              className="overflow-x-auto scrollbar-hide py-8 cursor-grab active:cursor-grabbing"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              onMouseEnter={() => setIsCarouselPaused(true)}
              onMouseLeave={() => setIsCarouselPaused(false)}
            >
              <div className="flex gap-6 sm:gap-10 px-4">
                {carouselBrands.map((brand, index) => (
                  <motion.div 
                    key={`${brand.id}-${index}`} 
                    className="flex-shrink-0 group"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="w-32 h-24 sm:w-40 sm:h-28 bg-white rounded-2xl p-4 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#fdaa01] flex items-center justify-center">
                      <img 
                        src={brand.logo} 
                        alt={brand.name}
                        className="max-w-full max-h-full object-contain grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                        title={brand.name}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setIsCarouselPaused(!isCarouselPaused)}
              className="absolute top-1/2 -translate-y-1/2 right-4 z-20 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-[#fdaa01] hover:shadow-xl transition-all duration-300"
            >
              {isCarouselPaused ? <FaPlay className="text-sm" /> : <FaPause className="text-sm" />}
            </button>
          </div>

          <motion.div 
            className="text-center pt-8 border-t border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-600 mb-4">
              Join these industry leaders in transforming your digital presence
            </p>
            <Link 
              to="/contact"
              className="group inline-flex items-center px-8 py-3 rounded-lg border-2 border-[#010536] text-[#010536] font-semibold hover:bg-[#010536] hover:text-white transition-all duration-300"
            >
              Become Our Partner
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Systems Section */}
      <section className="py-20 lg:py-28 bg-[#010536] relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(253,170,1,0.1)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
              <FaRocket className="mr-2" />
              Ready Solutions
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#fdaa01]">
              Industry-Specific Digital Systems
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto mt-4" />
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {readySystems.map((system, idx) => (
              <motion.div
                key={system.id}
                className="group bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#fdaa01] relative overflow-hidden"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
                whileHover={{ y: -10 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#010536] to-[#020a5c] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="relative z-10">
                  <div className="flex justify-between items-center mb-6">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500"
                      style={{ backgroundColor: 'rgba(253, 170, 1, 0.1)' }}
                    >
                      <div className="text-3xl text-[#fdaa01] group-hover:text-[#fdaa01] transition-colors duration-500">
                        {system.icon}
                      </div>
                    </div>
                    <div className="bg-gray-100 text-gray-800 px-4 py-1.5 rounded-full text-xs font-semibold group-hover:bg-white/20 group-hover:text-white transition-colors duration-500">
                      {system.category}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#fdaa01] transition-colors duration-500">
                    {system.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6 group-hover:text-white/90 transition-colors duration-500">
                    {system.description}
                  </p>
                  
                  <div className="mb-8 space-y-2">
                    {system.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center text-gray-700 group-hover:text-white/90 transition-colors duration-500">
                        <FaCheckCircle className="mr-3 flex-shrink-0 text-[#fdaa01]" />
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link 
                    to={`/system/${system.id}`}
                    className="inline-flex items-center justify-center w-full px-8 py-3 rounded-xl bg-gradient-to-r from-[#010536] to-[#020a5c] text-white font-semibold hover:from-[#fdaa01] hover:to-[#ffc042] hover:text-[#010536] hover:shadow-lg transition-all duration-300 group-hover:from-[#fdaa01] group-hover:to-[#ffc042] group-hover:text-[#010536]"
                  >
                    Explore System
                    <FaChevronRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/ready-systems">
              <motion.button
                className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View All Systems
                <FaArrowRight className="ml-2" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c] text-white relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(253,170,1,0.1)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
              <FaNetworkWired className="mr-2" />
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#fdaa01]">
              The Precision Digital Framework
            </h2>
            <p className="text-lg text-white/80 mt-4 max-w-2xl mx-auto">
              A proven 4-step process to ensure your digital initiatives deliver maximum impact and ROI.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, idx) => (
              <motion.div
                key={idx}
                className="group relative bg-white/5 backdrop-blur-sm border border-[#fdaa01]/20 rounded-3xl p-8 text-center hover:bg-[#fdaa01]/10 hover:border-[#fdaa01] transition-all duration-300"
                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.2, duration: 0.6 }}
                whileHover={{ y: -10 }}
              >
                <div className="absolute top-4 right-6 text-3xl font-extrabold text-[#fdaa01]/20 group-hover:text-[#fdaa01]/40 transition-colors duration-300">
                  {step.num}
                </div>
                
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#010536] to-[#020a5c] border border-[#fdaa01] mb-5 group-hover:scale-110 transition-transform duration-300">
                  <div className="text-[#fdaa01]">
                    {step.icon}
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-[#fdaa01] mb-3">{step.title}</h3>
                <p className="text-white/90 leading-relaxed mb-6">{step.desc}</p>
                
                <div className="text-left space-y-3">
                  {step.details.map((detail, dIdx) => (
                    <div key={dIdx} className="flex items-center text-white/80 text-sm group-hover:text-white transition-colors duration-300">
                      <FaChevronRight className="mr-2 text-[#fdaa01] flex-shrink-0" />
                      {detail}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(253,170,1,0.05)_0%,transparent_50%),radial-gradient(circle_at_70%_30%,rgba(1,5,54,0.05)_0%,transparent_50%)]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="bg-gradient-to-br from-[#010536] to-[#020a5c] text-white rounded-3xl p-8 sm:p-12 lg:p-16 text-center shadow-2xl border border-[#fdaa01]/10 relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <div className="absolute top-0 left-0 w-40 h-40 bg-[#fdaa01]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-[#fdaa01]/5 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <motion.div 
                className="inline-flex items-center bg-[#fdaa01]/10 text-[#fdaa01] px-6 py-2.5 rounded-full text-sm font-semibold mb-8 border border-[#fdaa01]/20"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <FaLightbulb className="mr-2 animate-pulse" />
                STRATEGY SESSION
              </motion.div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#fdaa01] mb-6">
                Ready to Dominate<br />
                Your Digital Space?
              </h2>
              
              <p className="text-lg text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed">
                Schedule a complimentary 60-minute digital strategy session with our lead consultants. 
                Receive actionable insights tailored to your business goals in Kenya's dynamic market.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                {[
                  'Free Competitive Analysis',
                  'Custom Growth Roadmap',
                  '30-Day Implementation Plan'
                ].map((feature, idx) => (
                  <motion.div 
                    key={idx}
                    className="flex items-center px-6 py-3 bg-[#fdaa01]/5 rounded-full font-semibold text-[#fdaa01] border border-[#fdaa01]/10 hover:bg-[#fdaa01]/10 transition-all duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <FaStar className="mr-2 text-[#fdaa01]" />
                    {feature}
                  </motion.div>
                ))}
              </div>
              
              <div className="flex flex-col items-center gap-8 mb-8">
                <motion.a
                  href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center px-10 py-5 rounded-xl bg-gradient-to-r from-[#fdaa01] to-[#ffc042] text-[#010536] font-semibold text-lg shadow-lg shadow-[#fdaa01]/30 hover:shadow-xl hover:shadow-[#fdaa01]/40 min-w-[300px] justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaCalendarAlt className="mr-3 group-hover:rotate-12 transition-transform duration-300" />
                  Book Strategy Session
                </motion.a>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="https://wa.me/+254762272152"
                    className="group inline-flex items-center justify-center px-8 py-4 rounded-lg bg-green-500 text-white font-semibold hover:bg-green-600 transition-all duration-300 min-w-[200px]"
                  >
                    <FaWhatsapp className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                    WhatsApp Chat
                  </a>
                  <a 
                    href="tel:+254762272152"
                    className="group inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#fdaa01] text-[#010536] font-semibold hover:bg-[#ffc042] transition-all duration-300 min-w-[200px]"
                  >
                    <FaPhoneAlt className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                    Call Now
                  </a>
                </div>
              </div>
              
              <div className="flex items-center justify-center gap-2 text-white/80 text-sm">
                <FaClock className="mr-2 animate-pulse text-[#fdaa01]" />
                Limited to 5 sessions per week for quality assurance
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Floating Contact */}
      <motion.div 
        className="fixed bottom-6 right-6 z-50"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 260, damping: 20 }}
      >
        <div className="flex flex-col gap-3">
          <a 
            href="https://wa.me/+254762272152"
            className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-green-500 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
          >
            <FaWhatsapp className="text-xl" />
            <span className="absolute right-16 bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-md">
              WhatsApp
            </span>
          </a>
          <a 
            href="tel:+254762272152"
            className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-[#fdaa01] text-[#010536] shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
          >
            <FaPhoneAlt className="text-xl" />
            <span className="absolute right-16 bg-white text-gray-900 px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-md">
              Call Us
            </span>
          </a>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="group flex items-center justify-center w-14 h-14 rounded-full bg-[#010536] text-[#fdaa01] border border-[#fdaa01] shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300"
          >
            <FaArrowUp className="text-xl group-hover:-translate-y-1 transition-transform duration-300" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Home;